/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { CONTRACT_RATE, ReferentPriceTypes } from '@strata/constants';

import { GO_LIVE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagTypes = { BATCHES: 'BATCHES', TREATMENT: 'TREATMENT' };

export const groupIdQueryParam = (groupId) => {
    if (!groupId?.length) {
        return undefined;
    }
    return groupId;
};

const mapFiltersForBulkActions = ({
    treatmentId = undefined,
    searchText,
    goLiveDate = [],
    nextFillNbd = [],
    productServiceOid = [],
    segmentId = [],
    incumbentSupplier = [],
    productType = [],
    isLDD,
    hasIssues = [],
    releaseStatus = [],
    upcomingTreatmentBatchName = [],
    preferredPharmacyOffer = [],
    therapeuticCategory = [],
    productGoLiveDate = [],
    treatmentStatus = [],
    onSegmentDrugList,
    groupId,
    // eslint-disable-next-line complexity
}) => ({
    treatment_id: treatmentId,
    search_text: searchText,
    go_live_date: goLiveDate.length > 0 ? goLiveDate : undefined,
    next_fill_nbd: nextFillNbd.length > 0 ? nextFillNbd : undefined,
    product_oid: productServiceOid.length > 0 ? productServiceOid : undefined,
    current_segment_id: segmentId.length > 0 ? segmentId : undefined,
    incumbent_supplier: incumbentSupplier.length > 0 ? incumbentSupplier : undefined,
    product_type: productType.length > 0 ? productType : undefined,
    is_ldd: isLDD?.[0],
    has_issues: hasIssues.length > 0 ? hasIssues : undefined,
    release_status: releaseStatus.length > 0 ? releaseStatus : undefined,
    batch_name: upcomingTreatmentBatchName.length > 0 ? upcomingTreatmentBatchName : undefined,
    parent_pharmacy_npi: preferredPharmacyOffer.length > 0 ? preferredPharmacyOffer : undefined,
    therapeutic_category: therapeuticCategory.length > 0 ? therapeuticCategory : undefined,
    product_go_live_date: productGoLiveDate.length > 0 ? productGoLiveDate : undefined,
    treatment_status: treatmentStatus.length > 0 ? treatmentStatus : undefined,
    on_segment_drug_list: onSegmentDrugList?.[0],
    group_id: groupIdQueryParam(groupId),
});

export const goLiveApi = createApi({
    baseQuery: getBaseQuery(GO_LIVE_API_URL),
    reducerPath: 'goLiveApi',
    tagTypes: Object.values(TagTypes),
    refetchOnMountOrArgChange: true,
    endpoints: (build) => ({
        getUpcomingTreatments: build.query({
            query: ({
                page,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
                sort,
            }) => ({
                url: '/upcoming-treatments',
                method: 'GET',
                params: {
                    page,
                    ...mapFiltersForBulkActions({
                        searchText,
                        productServiceOid,
                        goLiveDate,
                        nextFillNbd,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                    sort_col: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                },
            }),
            transformResponse: (response) => ({
                count: response?.count,
                filters: response?.filters,
                upcoming_treatments: response?.upcoming_treatments,
                incumbentMapping: response?.incumbent_mapping,
            }),
            providesTags: [TagTypes.TREATMENT],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving upcoming treatments.',
            ),
        }),
        getUpcomingTreatmentsMetadata: build.query({
            query: (filters) => ({
                url: '/upcoming-treatments/metadata',
                method: 'GET',
                params: mapFiltersForBulkActions(filters),
            }),
            transformResponse: (response) => ({
                count: response?.count,
                filters: response?.filters,
                incumbentMapping: response?.incumbent_mapping,
            }),
            providesTags: [TagTypes.TREATMENT],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving upcoming treatment metadata.',
            ),
        }),
        reprocessTreatment: build.mutation({
            query: ({
                treatmentId,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
            }) => ({
                url: '/upcoming-treatments/reprocess',
                method: 'POST',
                body: {
                    filters: mapFiltersForBulkActions({
                        treatmentId,
                        searchText,
                        goLiveDate,
                        nextFillNbd,
                        productServiceOid,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                },
            }),
            invalidatesTags: (_, error) => (!error ? [TagTypes.TREATMENT] : undefined),
        }),
        incrementalBatchRelease: build.mutation({
            query: ({ batchIds, timeInterval, quantityPerRelease }) => ({
                url: 'upcoming-treatment-batches/incremental-release',
                method: 'POST',
                body: {
                    batch_ids: batchIds,
                    time_interval: timeInterval,
                    quantity_per_release: quantityPerRelease,
                },
            }),
            invalidatesTags: (_, error) => (!error ? [TagTypes.TREATMENT] : undefined),
        }),
        setPreferredPharmacyOffer: build.mutation({
            query: ({
                treatmentId,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
                // Params for offer_info
                supplierNpi,
                transitionSupplier,
                referentPriceType,
                offerRate,
                grandfathered,
                referralExpirationStrategy,
                referralExpirationReferenceDate,
                expireInSetsOf,
                priceIsFrozen,
                routeCode,
                useContractRate,
                forceTransferRoute,
                sort,
            }) => ({
                url: '/upcoming-treatments/offer',
                method: 'POST',
                params: {
                    sort_col: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                },
                body: {
                    offer_info: {
                        parent_pharmacy_npi: supplierNpi,
                        transition_supplier: transitionSupplier,
                        referent_price_type: useContractRate
                            ? ReferentPriceTypes.AWP
                            : referentPriceType,
                        discount: useContractRate ? CONTRACT_RATE : offerRate,
                        grandfathered,
                        referral_expiration_strategy: referralExpirationStrategy,
                        referral_expiration_reference_date: referralExpirationReferenceDate,
                        expire_in_sets_of: expireInSetsOf,
                        price_is_frozen: priceIsFrozen,
                        route_code: routeCode,
                        allow_route_override: !forceTransferRoute,
                    },
                    filters: mapFiltersForBulkActions({
                        treatmentId,
                        searchText,
                        goLiveDate,
                        nextFillNbd,
                        productServiceOid,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                },
            }),
            invalidatesTags: (_, error) => (!error ? [TagTypes.TREATMENT] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'Unable to set preferred pharmacy offer. Please try again.',
            ),
        }),
        removePreferredPharmacyOffer: build.mutation({
            query: ({
                treatmentId,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
            }) => ({
                url: '/upcoming-treatments/clear-offer',
                method: 'POST',
                body: {
                    filters: mapFiltersForBulkActions({
                        treatmentId,
                        searchText,
                        goLiveDate,
                        nextFillNbd,
                        productServiceOid,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                },
            }),
            invalidatesTags: (_, error) => (!error ? [TagTypes.TREATMENT] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'Unable to remove preferred pharmacy offer(s). Please try again.',
            ),
        }),
        addToBatch: build.mutation({
            query: ({
                batch,
                treatmentId,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
            }) => ({
                url: `/upcoming-treatments/batch`,
                method: 'POST',
                body: {
                    batch_id: batch.batchId,
                    batch_name: batch.name,
                    filters: mapFiltersForBulkActions({
                        treatmentId,
                        searchText,
                        goLiveDate,
                        nextFillNbd,
                        productServiceOid,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                },
            }),
            invalidatesTags: (_, error) =>
                !error ? [TagTypes.BATCHES, TagTypes.TREATMENT] : undefined,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred batching upcoming treatments.',
            ),
        }),
        removeFromBatch: build.mutation({
            query: ({
                treatmentId,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
            }) => ({
                url: `/upcoming-treatments/unbatch`,
                method: 'POST',
                body: {
                    filters: mapFiltersForBulkActions({
                        treatmentId,
                        searchText,
                        goLiveDate,
                        nextFillNbd,
                        productServiceOid,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                },
            }),
            invalidatesTags: (_, error) =>
                !error ? [TagTypes.BATCHES, TagTypes.TREATMENT] : undefined,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred unbatching upcoming treatments.',
            ),
        }),
        getBatches: build.query({
            query: () => '/upcoming-treatment-batches',
            providesTags: [TagTypes.BATCHES],
            transformResponse: (response) =>
                (response ?? []).map((batch) => ({
                    batchId: batch.batch_id,
                    name: batch.batch_name,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving batches.'),
        }),
        getBatchesAndFilteringCriteria: build.query({
            query: () => '/upcoming-treatment-batches/batches-and-filter-criteria',
            providesTags: [TagTypes.BATCHES],
            transformResponse: (response) => ({
                batches: response?.batches ?? [],
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving batches.'),
        }),
        deleteBatch: build.mutation({
            query: ({ batchId }) => ({
                url: `/upcoming-treatment-batches/${batchId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) =>
                !error ? [TagTypes.BATCHES, TagTypes.TREATMENT] : undefined,
            transformErrorResponse: handleRTKQueryError('An error occurred deleting the batch.'),
        }),
        renameBatch: build.mutation({
            query: ({ batchId, batchName }) => ({
                url: `/upcoming-treatment-batches/${batchId}`,
                method: 'PATCH',
                body: { batch_name: batchName },
            }),
            invalidatesTags: (_, error) =>
                !error ? [TagTypes.BATCHES, TagTypes.TREATMENT] : undefined,
            transformErrorResponse: handleRTKQueryError('An error occurred renaming the batch.'),
        }),
        runRouteSimulator: build.mutation({
            query: ({
                treatmentId,
                searchText,
                goLiveDate,
                nextFillNbd,
                productServiceOid,
                segmentId,
                incumbentSupplier,
                productType,
                isLDD,
                hasIssues,
                releaseStatus,
                upcomingTreatmentBatchName,
                preferredPharmacyOffer,
                therapeuticCategory,
                productGoLiveDate,
                treatmentStatus,
                onSegmentDrugList,
                groupId,
            }) => ({
                url: `/simulations`,
                method: 'POST',
                body: {
                    filters: mapFiltersForBulkActions({
                        treatmentId,
                        searchText,
                        goLiveDate,
                        nextFillNbd,
                        productServiceOid,
                        segmentId,
                        incumbentSupplier,
                        productType,
                        isLDD,
                        hasIssues,
                        releaseStatus,
                        upcomingTreatmentBatchName,
                        preferredPharmacyOffer,
                        therapeuticCategory,
                        productGoLiveDate,
                        treatmentStatus,
                        onSegmentDrugList,
                        groupId,
                    }),
                },
            }),
            invalidatesTags: (_, error) => (!error ? [TagTypes.TREATMENT] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred starting route simulation.',
            ),
        }),
        simulateSingleTreatment: build.mutation({
            query: ({ treatmentId }) => ({
                url: `/simulations/treatment/${treatmentId}`,
                method: 'POST',
            }),
            invalidatesTags: (_, error) => (!error ? [TagTypes.TREATMENT] : undefined),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred starting route simulation.',
            ),
        }),
    }),
});

export const {
    useGetUpcomingTreatmentsQuery,
    useGetUpcomingTreatmentsMetadataQuery,
    useReprocessTreatmentMutation,
    useIncrementalBatchReleaseMutation,
    useSetPreferredPharmacyOfferMutation,
    useRemovePreferredPharmacyOfferMutation,
    useAddToBatchMutation,
    useRemoveFromBatchMutation,
    useGetBatchesQuery,
    useGetBatchesAndFilteringCriteriaQuery,
    useDeleteBatchMutation,
    useRenameBatchMutation,
    useRunRouteSimulatorMutation,
    useSimulateSingleTreatmentMutation,
} = goLiveApi;
